import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CustomSearch,
  DisplayItemsPerPageSelect,
  Paginator,
  SecondaryButton,
} from 'components';
import { AdminList } from '../AdminList';
import { StyledAdminEditUsers } from './styles';
import { DataExportButton } from '../DataExportButton';
import { RootState, useAppDispatch, useAppSelector } from 'store/store';
import { useSelector } from 'react-redux';
import {
  selectAdminUsers,
  setAverageDailyHashrateFilter,
  setAverageWeeklyHashrateFilter,
  setCreatedAtDateFilter,
  setItemsPerUsersListPage,
  setSearch,
  setSort,
  setUpdatedAtDateFilter,
  setVipStatusFilter,
  usersListSetPage,
} from 'store/slices/adminUsersSlice';
import { fetchExportUsers, fetchUsers } from 'store/thunks/adminUsersThunk';
import { IAdminUser, TSortAdminUserByHeaderTitle } from 'types';
import { AdminRepository } from 'repositories/AdminRepository';
import {
  VALIDATION_ERROR_ADMIN_USER_ADJUST_REQUIRED,
  VALIDATION_ERROR_ADMIN_USER_COST_REQUIRED,
} from 'constants/errorsConstants';
import { AdminEditInput } from '../AdminEditInput';
import { AdminUsersListHeader } from '../AdminUsersListHeader';
import { AdminUsersListRow } from '../AdminUsersListRow';
import { RefreshButton } from '../RefreshButton';
import { AdminConfirmModal } from '../AdminConfirmModal';
import {
  downloadCsvFile,
  getFormattedServerDateTimeRequest,
  getNextSortDir,
  parseHashrateValueRange,
  updateUsers,
} from 'utils';

interface IAdminEditUsersProps {
  children?: React.ReactNode;
}

export const AdminEditUsers = ({ children }: IAdminEditUsersProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { users } = useSelector(selectAdminUsers);
  const { token } = useAppSelector((state) => state.auth);
  const { newbieCost, newbieAdjust } = useAppSelector((state) => state.adminCosts);
  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedUsersIds, setSelectedUsersIds] = useState<IAdminUser['id'][]>([]);
  const [showVipModal, setShowVipModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const { pagesQuantity, currentPage, itemsPerPage, sortBy, sortDir } = useSelector(
    (state: RootState) => state.adminUsers,
  );

  const submitEditVipStatusHandler = () => {
    setShowVipModal(true);
  };

  const submitEditBanStatusHandler = () => {
    setShowBanModal(true);
  };

  const submitVipAction = () => {
    submitEditUserVipStatus();
    setShowVipModal(false);
  };

  const submitBanAction = () => {
    submitEditUserBanStatus();
    setShowBanModal(false);
  };

  const closeVipModal = () => {
    setShowVipModal(false);
  };

  const closeBanModal = () => {
    setShowBanModal(false);
  };

  const setSortHandler = (sortBy: {
    sortBy: keyof IAdminUser;
    sortDir: 'ASC' | 'DESC' | 'NONE';
  }) => {
    dispatch(
      setSort({
        sortBy: sortBy.sortDir === 'NONE' ? null : sortBy.sortBy,
        sortDir: sortBy.sortDir === 'NONE' ? null : sortBy.sortDir,
      }),
    );
    dispatch(fetchUsers());
  };

  const sortHandler = (nextSortBy: TSortAdminUserByHeaderTitle) => {
    const nextSortDir = getNextSortDir(sortBy, nextSortBy, sortDir as 'ASC' | 'DESC' | 'NONE');
    setSortHandler({ sortBy: nextSortBy, sortDir: nextSortDir });
  };

  const filterByValueHandler = (filterByValue: {
    filterBy: keyof IAdminUser;
    filterValue: string;
  }) => {
    const { filterBy, filterValue } = filterByValue;

    switch (filterBy) {
      case 'vip':
        dispatch(setVipStatusFilter(filterValue === '-' ? null : String(filterValue === 'vip')));
        break;
      case 'averageDailyHashRate':
        dispatch(setAverageDailyHashrateFilter(parseHashrateValueRange(filterValue)));
        break;
      case 'averageWeeklyHashRate':
        dispatch(setAverageWeeklyHashrateFilter(parseHashrateValueRange(filterValue)));
        break;
      default:
        return;
    }

    dispatch(fetchUsers());
  };

  const filterByDateHandler = (filterByDate: {
    filterBy: keyof IAdminUser;
    fromDate: Date;
    toDate: Date;
  }) => {
    const filterFromDate = getFormattedServerDateTimeRequest(String(filterByDate.fromDate));
    const filterToDate = getFormattedServerDateTimeRequest(String(filterByDate.toDate));

    switch (filterByDate.filterBy) {
      case 'createdAt':
        dispatch(
          setCreatedAtDateFilter({
            fromDate: filterFromDate === filterToDate ? null : filterFromDate,
            toDate: filterFromDate === filterToDate ? null : filterToDate,
          }),
        );
        break;
      case 'updatedAt':
        dispatch(
          setUpdatedAtDateFilter({
            fromDate: filterFromDate === filterToDate ? null : filterFromDate,
            toDate: filterFromDate === filterToDate ? null : filterToDate,
          }),
        );
        break;
      default:
        return;
    }

    dispatch(fetchUsers());
  };

  const setSearchFilterHandler = (value: string) => {
    dispatch(setSearch(value === '' ? null : value));
    dispatch(fetchUsers());
  };

  const refreshButtonHandler = () => {
    dispatch(fetchUsers());
  };

  const dataExportButtonHandler = async () => {
    const data = await dispatch(fetchExportUsers()).unwrap();
    downloadCsvFile(data.exportData, 'Users');
  };

  const changeCurrentPageHandler = (currentPage: number) => {
    dispatch(usersListSetPage(currentPage));
    dispatch(fetchUsers());
  };

  const changeItemsPerPageQuantityHandler = (itemsQuantity: number) => {
    dispatch(setItemsPerUsersListPage(itemsQuantity));
    dispatch(fetchUsers());
  };

  const selectAllHandler = (isSelectedAll: boolean) => {
    if (isSelectedAll) {
      const allIds = users.map((el) => el.id);
      setSelectedUsersIds(allIds);
      setSelectedAll(true);
    } else {
      setSelectedUsersIds([]);
      setSelectedAll(false);
    }
  };

  const selectHanler = (id: IAdminUser['id'], selected: boolean) => {
    if (selected) {
      setSelectedUsersIds((prev) => [...prev, id]);
    } else {
      const updatedSelectedElementsIds = selectedUsersIds.filter((selectedId) => selectedId !== id);
      setSelectedUsersIds(updatedSelectedElementsIds);
    }
  };

  const submitEditUserCost = async (amount: number) => {
    if (selectedUsersIds.length === 0) return;

    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof amount !== 'number') throw Error(VALIDATION_ERROR_ADMIN_USER_COST_REQUIRED);

      const filteredUsers = users.filter((user) => Number(user.cost) !== amount);
      const filteredUserIds = selectedUsersIds.filter((id) =>
        filteredUsers.some((user) => user.id === id),
      );

      await updateUsers(filteredUserIds, filteredUsers, (user: IAdminUser) =>
        AdminRepository.setUserCost(token, {
          userId: Number(user.id),
          cost: amount,
        }),
      );

      await dispatch(fetchUsers());
      setSelectedUsersIds([]);
      setSelectedAll(false);
    } catch (error) {
      console.error('submit user cost error', error);
    }
  };

  const submitEditUserAdjust = async (amount: number) => {
    if (selectedUsersIds.length === 0) return;

    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof amount !== 'number') throw Error(VALIDATION_ERROR_ADMIN_USER_ADJUST_REQUIRED);

      const filteredUsers = users.filter((user) => Number(user.adjust) !== amount);
      const filteredUserIds = selectedUsersIds.filter((id) =>
        filteredUsers.some((user) => user.id === id),
      );

      await updateUsers(filteredUserIds, filteredUsers, (user: IAdminUser) =>
        AdminRepository.setUserAdjust(token, {
          userId: Number(user.id),
          adjust: amount,
        }),
      );

      await dispatch(fetchUsers());
      setSelectedUsersIds([]);
      setSelectedAll(false);
    } catch (error) {
      console.error('submit user adjust error', error);
    }
  };

  const submitEditUserVipStatus = async () => {
    if (selectedUsersIds.length === 0) return;

    try {
      if (!token) throw Error('no token or unathorised');

      await updateUsers(selectedUsersIds, users, (user: IAdminUser) =>
        AdminRepository.setUserVipStatus(token, {
          userId: Number(user.id),
          vip: !user.vip,
        }),
      );

      await dispatch(fetchUsers());
      setSelectedUsersIds([]);
      setSelectedAll(false);
    } catch (error) {
      console.error('submit user vip status error', error);
    }
  };

  const submitEditUserBanStatus = async () => {
    if (selectedUsersIds.length === 0) return;

    try {
      if (!token) throw Error('no token or unathorised');

      await updateUsers(selectedUsersIds, users, (user: IAdminUser) =>
        AdminRepository.setUserBanStatus(token, {
          userId: Number(user.id),
        }),
      );

      await dispatch(fetchUsers());
      setSelectedUsersIds([]);
      setSelectedAll(false);
    } catch (error) {
      console.error('submit user ban status error', error);
    }
  };

  useEffect(() => {
    if (selectedAll && selectedUsersIds.length !== users.length) setSelectedAll(false);
  }, [selectedAll, selectedUsersIds]);

  return (
    <StyledAdminEditUsers>
      <AdminList
        renderControls={() => (
          <div className="admin-edit-users-controls">
            <AdminEditInput
              onSubmit={submitEditUserCost}
              heading={t('adminPage.editUsers.changeCost')}
              modalHeading={t('adminPage.confirmModal.costChange')}
              cost={newbieCost || 0}
            />
            <AdminEditInput
              onSubmit={submitEditUserAdjust}
              heading={t('adminPage.changeAdjust')}
              modalHeading={t('adminPage.confirmModal.adjustChange')}
              cost={newbieAdjust || 0}
            />
            <Button
              title={t('adminPage.editUsers.toggleVip')}
              onClick={submitEditVipStatusHandler}
            />
            {showVipModal && (
              <AdminConfirmModal
                title={t('adminPage.confirmModal.vipChange')}
                onConfirm={submitVipAction}
                onCancel={closeVipModal}
                show={showVipModal}
              />
            )}
            <SecondaryButton
              title={t('adminPage.editUsers.ban')}
              outline
              onClick={submitEditBanStatusHandler}
            />
            {showBanModal && (
              <AdminConfirmModal
                title={t('adminPage.confirmModal.banChange')}
                onConfirm={submitBanAction}
                onCancel={closeBanModal}
                show={showBanModal}
              />
            )}
            <div className="admin-edit-users-controls__right">
              <CustomSearch onChange={setSearchFilterHandler} />
              <DataExportButton onClick={dataExportButtonHandler} />
              <RefreshButton onClick={refreshButtonHandler} />
            </div>
          </div>
        )}
      >
        <div className="admin-edit-users-list-body">
          <AdminUsersListHeader
            onSelectAll={selectAllHandler}
            selectedAll={selectedAll}
            onSort={sortHandler}
            onFilterByDate={filterByDateHandler}
            onFilterByValue={filterByValueHandler}
            sortBy={sortBy}
            sortDir={sortDir as 'ASC' | 'DESC' | 'NONE'}
          />
          {users.map((user) => (
            <AdminUsersListRow
              key={user.id}
              selected={selectedUsersIds.includes(user.id)}
              onSelect={selectHanler}
              user={user}
            />
          ))}
        </div>
      </AdminList>
      {pagesQuantity > 0 && (
        <div className="list-body-pagination">
          <Paginator
            onChange={changeCurrentPageHandler}
            pageQuantity={pagesQuantity}
            currentPage={currentPage}
          />
          <DisplayItemsPerPageSelect
            onChange={changeItemsPerPageQuantityHandler}
            currentItemsPerPageQuantity={itemsPerPage}
            itemsPerPageRates={[10, 15, 25, 50]}
          />
        </div>
      )}
    </StyledAdminEditUsers>
  );
};
